import React from 'react';
import { HomeIcon, MapPinIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { CtaBand } from '../components/ui/CtaBand';
import { SectionHeading } from '../components/ui/SectionHeading';
import { labPanels } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
const markerGroups = [{
  group: 'Hormones',
  markers: ['Total testosterone', 'Free testosterone', 'Estradiol', 'SHBG', 'LH', 'FSH', 'DHEA-S', 'Progesterone']
}, {
  group: 'Metabolic',
  markers: ['HbA1c', 'Fasting glucose', 'Fasting insulin', 'ApoB', 'Lp(a)', 'Full lipid panel', 'Uric acid']
}, {
  group: 'Thyroid & inflammation',
  markers: ['TSH', 'Free T3', 'Free T4', 'TPO antibodies', 'hs-CRP', 'Homocysteine', 'Ferritin']
}, {
  group: 'Organ & blood',
  markers: ['CBC with differential', 'CMP', 'eGFR', 'ALT / AST', 'PSA (men)', 'Vitamin D', 'B12']
}];
export function Labs() {
  const {
    brand
  } = useBrand();
  useDocumentMeta({
    title: `Labs & testing — ${brand.name}`,
    description: 'A 62-marker baseline panel included with every membership, collected at home or at 2,100 partner draw sites.'
  });
  return <main data-id="element-403" data-mp="xa4bmdg50a00">
      <PageHeader eyebrow="Labs & testing" title="The panel is the foundation, not an upsell." intro="Republic includes a baseline panel with every membership and annotates the results in plain language before any treatment decision is made." data-id="element-404" data-mp="126v1ttlnolz5" />

      <section className="bg-cream" data-id="element-405" data-mp="9k9zxz7k0rhc">
        <div className="mx-auto max-w-shell px-6 pb-20 pt-10 md:px-8 md:pb-28" data-id="element-406" data-mp="egr05rs12was">
          <img src={brand.images.labKit} alt={`A ${brand.name} at-home collection kit`} className="aspect-[16/7] w-full rounded-brand object-cover" data-id="element-407" data-mp="iivqohn6jct7" />

          <ul className="mt-14 grid gap-8 lg:grid-cols-3" data-id="element-408" data-mp="1ty0vpsas9nfz">
            {labPanels.map(panel => <li key={panel.name} className="rule pt-6" data-id="element-409" data-mp="3qyn8an2x8vea">
                <div className="flex items-baseline justify-between gap-4" data-id="element-410" data-mp="3qx3poptsm89c">
                  <h2 className="font-serif text-2xl text-ink" data-id="element-411" data-mp="3lyipurigdqlf">{panel.name}</h2>
                  <span className="font-serif text-3xl text-brick" data-id="element-412" data-mp="3uh5k2zb1ffx">{panel.markers}</span>
                </div>
                <p className="mt-3 text-base leading-relaxed text-ink-soft" data-id="element-413" data-mp="3v00dqw23qwko">{panel.detail}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-414" data-mp="3392oy32uvw4h">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-28" data-id="element-415" data-mp="33a42qfb9awpw">
          <SectionHeading eyebrow="What we measure" title="Every marker, published before you book." intro="No panel at Republic contains a marker we cannot explain the clinical reason for." data-id="element-416" data-mp="3h10rtw5z4vs7" />
          <div className="mt-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4" data-id="element-417" data-mp="2oilp2o5vde9p">
            {markerGroups.map(group => <div key={group.group} data-id="element-418" data-mp="2jj0ns7388ttt">
                <h3 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-419" data-mp="z2j1j22jm9mx">
                  {group.group}
                </h3>
                <ul className="mt-5" data-id="element-420" data-mp="13v2fi05576s9">
                  {group.markers.map(marker => <li key={marker} className="rule py-3 text-sm text-ink-soft" data-id="element-421" data-mp="q3xotnjr5ou2">
                      {marker}
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      <section className="bg-cream" data-id="element-422" data-mp="1mt5acvkdfxxz">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-423" data-mp="1mrbz12ytcpjn">
          <SectionHeading eyebrow="Collection" title="Two ways to get your blood drawn." data-id="element-424" data-mp="18tnejw8nrkf4" />
          <div className="mt-12 grid gap-8 md:grid-cols-2" data-id="element-425" data-mp="1drfgzk6xioxk">
            {[{
            icon: HomeIcon,
            title: 'At home',
            detail: 'A kit ships within one business day. Collect in ten minutes and return it in the prepaid mailer. Results in three to five days.',
            meta: 'Included with membership'
          }, {
            icon: MapPinIcon,
            title: 'At a partner site',
            detail: 'Book a draw at one of 2,100 partner locations, usually same week. Fasting appointments available before 10am.',
            meta: 'Included with membership'
          }].map(option => <div key={option.title} className="rounded-brand border border-ink/15 bg-cream-light p-8" data-id="element-426" data-mp="10khxdirops7s">
                <option.icon className="h-6 w-6 text-ink" strokeWidth={1.4} data-id="element-427" data-mp="whlb9i2xrdz3" />
                <h3 className="mt-6 font-serif text-2xl text-ink" data-id="element-428" data-mp="rj1ndc4s7s43">{option.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-ink-soft" data-id="element-429" data-mp="mkgpvkgwzmjc">{option.detail}</p>
                <p className="mt-6 text-[0.6875rem] uppercase tracking-label text-brick" data-id="element-430" data-mp="3cbvc935mt3vd">
                  {option.meta}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      <CtaBand title="Get the numbers first." intro="Panels are available without a membership if you would rather see your data before deciding on treatment." data-id="element-431" data-mp="2x9die39l2dmy" />
    </main>;
}